import { useNavigate, useParams } from 'react-router-dom'
import { ArrowRight2 } from 'iconsax-react'
import { Info } from 'lucide-react'
import { Seller } from '../../../../../../app/entities/Seller'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../../components/ui/tooltip'
import { BasicAvatar } from '../../../../../components/BasicAvatar'
import {
  UserRoleDicionary,
  UserRoleEnum,
} from '@/app/services/usersService/user'
import { useState } from 'react'
import { RoleSelectionModal } from './RoleSelectionModal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usersService } from '@/app/services/usersService'
import { useAuth } from '@/app/hooks/useAuth'
import { cn } from '@/app/utils/cn'
import { formatMoneyValue } from '@/app/utils/formatMoneyValue'
import { WithdrawService } from '@/app/services/WithdrawService'
import { Spinner } from '@/view/components/ui/spinner'

export const SellerCardDetails = ({
  name,
  profileImageUrl,
  transactions,
  team,
  role,
  secondRole,
}: Seller) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false)
  const queryClient = useQueryClient()
  const { isSuperAdmin, isAdmin } = useAuth()

  const handleGoToTeamPage = (teamId: string | undefined) => {
    if (teamId) {
      navigate(`/times/detalhes/${teamId}`)
    }
  }

  const { mutateAsync } = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRoleEnum }) =>
      usersService.userChangRole(id, role),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ['sellerInfo', id],
      })
    },
  })

  const { data: availableAmount, isLoading: amountIsLoading } = useQuery({
    enabled: isAdmin && !!id,
    queryKey: ['getAvailvableAmount', id],
    queryFn: () => WithdrawService.getAvailableAmount(id as string),
    select: (data) => data.amount,
  })

  const handleRoleSelect = async (selectedRole?: UserRoleEnum) => {
    try {
      if (id) {
        await mutateAsync({
          id: id!,
          role: selectedRole ?? role,
        })
      }
      setIsRoleModalOpen(false)
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <div
      className={`flex w-full flex-col gap-2 rounded-lg bg-white02 p-5 pb-3`}
    >
      <div className="flex justify-between">
        <div className="flex w-full flex-wrap items-start justify-between gap-2">
          <BasicAvatar name={name} profileImageUrl={profileImageUrl} />
          <div className="flex items-center gap-2">
            <div
              onClick={() => isSuperAdmin && setIsRoleModalOpen(true)}
              className={cn(
                'h-fit w-fit  rounded-3xl bg-info/15 px-2 py-1 transition-colors hover:bg-info/30',
                isSuperAdmin && 'cursor-pointer',
              )}
            >
              <p className="text-[10px] font-semibold text-info">
                {UserRoleDicionary[role]}
              </p>
            </div>
            {secondRole && (
              <div
                onClick={() => isSuperAdmin && setIsRoleModalOpen(true)}
                className={cn(
                  'h-fit w-fit  rounded-3xl bg-primary/15 px-2 py-1 transition-colors hover:bg-primary/30',
                  isSuperAdmin && 'cursor-pointer',
                )}
              >
                <p className="text-[10px] font-semibold text-primary">
                  {UserRoleDicionary[secondRole]}
                </p>
              </div>
            )}
            {team?.name && (
              <>
                <div className="h-5 w-[1px] border-[1px] border-r-[#E5E5E5]" />
                <div
                  onClick={() => handleGoToTeamPage(team?._id)}
                  className="flex cursor-pointer items-center gap-2"
                >
                  <i className="fa-solid fa-people-group text-black04"></i>
                  <p className="text-xs font-semibold text-black04">
                    {team?.name}
                  </p>
                  <ArrowRight2 size="16" className="text-black04" />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <p className="text-2xl font-semibold">{name}</p>

      <div className="flex items-center gap-2">
        <p className="text-xs font-medium text-[#747474]">
          {transactions.lastMonth ? transactions.lastMonth : 0} saques no último
          mês
        </p>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info size={16} color="black" />
            </TooltipTrigger>
            <TooltipContent className="w-52 rounded-xl bg-black">
              <p className="text-xs text-white">
                O <b>&quot;SAQUE&quot;</b> é a transferência dos seus ganhos de
                comissão para sua conta bancária. Este processo pode levar até 8
                dias para ser aprovado.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {isAdmin && (
        <TooltipProvider>
          <Tooltip>
            {amountIsLoading ?
              (<div className='flex items-start p-2'>
                <Spinner size="small" />
              </div>

              )
              : (
                <TooltipTrigger
                  className={cn(
                    'mt-2 h-fit w-fit rounded-3xl bg-error/15 px-2 py-1',
                    availableAmount && availableAmount > 0 && 'bg-success/15',
                  )}
                >

                  <p
                    className={cn(
                      'text-sm font-bold text-error',
                      availableAmount && availableAmount > 0 && 'text-success',
                    )}
                  >
                    {formatMoneyValue(availableAmount || 0)}
                  </p>
                </TooltipTrigger>
              )}
            <TooltipContent
              className="rounded-xl bg-black/85 px-5 py-2"
              side="bottom"
            >
              <p className="text-xs text-white">
                Saldo disponivel no APP para saque
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <RoleSelectionModal
        open={isRoleModalOpen}
        onOpenChange={setIsRoleModalOpen}
        onRoleSelect={handleRoleSelect}
      />
    </div>
  )
}
